import React, { forwardRef, useImperativeHandle } from 'react';
import { BankStatementExtractRequest, AccountTypeDefinition, FieldDefinition } from '../types';
import { features } from '../config';

interface FormDataWithAuth extends Omit<BankStatementExtractRequest, 'asset_id'> {
  auth_token: string;
  custom_section_enabled: boolean;
  custom_section_title: string;
  custom_section_description: string;
}

interface BankStatementExtractRequestFormProps {
  formData: FormDataWithAuth;
  onFormDataChange: (formData: FormDataWithAuth) => void;
  onSubmit: (request: Omit<BankStatementExtractRequest, 'asset_id'>, authToken: string) => void;
  disabled?: boolean;
  onValidationChange?: (isValid: boolean) => void;
}

export interface BankStatementExtractRequestFormRef {
  isValid: () => boolean;
}

const ACCOUNT_TYPE_TEMPLATES = {
  basic: {
    displayName: 'Basic',
    definitions: [
      { name: "saving_account" },
      { name: "credit_card_account" },
      { name: "investment_account" },
      { name: "business_account" },
    ] as AccountTypeDefinition[],
  },
  current_account: {
    displayName: "Current Account",
    definitions: [
      { name: "current_account" },
    ] as AccountTypeDefinition[],
  }
};

const FIELD_TEMPLATES = {
  basic: {
    displayName: 'Basic',
    definitions: [
      {
        name: "statement_date",
        field_type: "date",
        description: "Issued date of the statement, leave null if not present."
      },
      {
        name: "transaction_date",
        field_type: "date",
        required: true
      },
      {
        name: "description",
        description: "Usually include the transaction unique identifier",
        required: true
      },
      {
        name: "currency",
        description: "Currency used for the transaction amount values, leave null if not present."
      },
      {
        name: "deposit_amount",
        field_type: "number",
        description: "Also known as credit or credit amount"
      },
      {
        name: "withdrawal_amount",
        field_type: "number",
        description: "Also known as debit or debit amount, often accompanied by a minus sign in the record"
      },
      {
        name: "account_balance",
        field_type: "number"
      },
    ] as FieldDefinition[]
  },
  arabic: {
    displayName: 'Arabic',
    definitions: [
      {
        name: "date",
        field_type: "date",
        required: true,
        description: "Date of the transaction record"
      },
      {
        name: "description_lines",
        description: "For multi-line description, return each line as an individual item. For Arabic words, return in the EXACT SAME WORD ORDER as shown in the image",
        required: true,
        is_list: true
      },
      {
        name: "withdrawals_and_debits",
        field_type: "number",
        description: "often accompanied by a minus sign in the record. DO NOT return the minus sign"
      },
      {
        name: "deposits_and_credits",
        field_type: "number"
      },
      {
        name: "balance",
        field_type: "number",
        description: "Can take negative value."
      },
      {
        name: "transaction_type",
        description: "Type of the transaction, return \"others\" if not sure.",
        enum_values: [
            "payroll", "income", "operating_expenses", "others"
        ],
        required: true
      }
    ] as FieldDefinition[],
  }
};

const CUSTOM_INSTRUCTION_TEMPLATES = {
  basic: {
    displayName: 'Basic',
    title: 'Points to Note',
    description: '1.  Page contents are captured from OCR and you must adapt to potential OCR error of transaction keywords.\n2.  Bank statement usually starts with an account summary or financial overview section. DO NOT extract transactions from this section.',
  },
  arabic: {
    displayName: 'Arabic',
    title: 'Points to Note',
    description: '1.  Page contents are captured from OCR and you must adapt to potential OCR error of transaction keywords.\n2.  Bank statement usually starts with an account summary or financial overview section. DO NOT extract transactions from this section.\n3.  For arabic characters, ALWAYS follow the visual word order in the image. \n    -  e.g. If the page text shows `مدى شبكة` but the image shows `شبكة مدى`, follow the image order and return `شبكة مدى` as the description.',
  },
  additional_instructions: {
    displayName: 'Additional Instructions',
    title: 'Additional Instructions',
    description: '1. Remove unnecessary whitespace between CJK characters for string fields.\n2. Use null for unsure field.\n3. Unless requested, extract information strictly from the image and its OCR text, retaining the original language as it appears.'
  },
};


export const BankStatementExtractRequestForm = forwardRef<BankStatementExtractRequestFormRef, BankStatementExtractRequestFormProps>(({
  formData,
  onFormDataChange,
  onSubmit,
  disabled,
  onValidationChange
}, ref) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const { auth_token, custom_section_enabled, custom_section_title, custom_section_description, ...requestData } = formData;
    const customSection = custom_section_enabled && custom_section_title.trim() && custom_section_description.trim()
      ? `# ${custom_section_title.trim()}\n\n${custom_section_description.trim()}`
      : null;

    // Only include args in the request when enabled; otherwise send null
    const payload = {
      ...requestData,
      custom_section: customSection,
      chat_complete_request_args: chatArgsEnabled ? requestData.chat_complete_request_args : null,
    };
    onSubmit(payload, auth_token);
  };

  const updateFormData = (updates: Partial<FormDataWithAuth>) => {
    onFormDataChange({ ...formData, ...updates });
  };

  const addAccountType = () => {
    updateFormData({
      account_type_definitions: [...formData.account_type_definitions, { name: '' }]
    });
  };

  const updateAccountType = (index: number, field: keyof AccountTypeDefinition, value: string) => {
    const updatedDefinitions = formData.account_type_definitions.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    updateFormData({ account_type_definitions: updatedDefinitions });
  };

  const removeAccountType = (index: number) => {
    const updatedDefinitions = formData.account_type_definitions.filter((_, i) => i !== index);
    updateFormData({ account_type_definitions: updatedDefinitions });
  };

  const addField = () => {
    updateFormData({
      field_definitions: [...formData.field_definitions, { name: '', field_type: 'string', required: false }]
    });
  };

  const updateField = (index: number, field: keyof FieldDefinition, value: any) => {
    const updatedDefinitions = formData.field_definitions.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    updateFormData({ field_definitions: updatedDefinitions });
  };

  const removeField = (index: number) => {
    const updatedDefinitions = formData.field_definitions.filter((_, i) => i !== index);
    updateFormData({ field_definitions: updatedDefinitions });
  };

  const loadAccountTemplate = (templateKey: keyof typeof ACCOUNT_TYPE_TEMPLATES) => {
    updateFormData({ account_type_definitions: ACCOUNT_TYPE_TEMPLATES[templateKey].definitions });
  };

  const loadFieldTemplate = (templateKey: keyof typeof FIELD_TEMPLATES) => {
    updateFormData({ field_definitions: FIELD_TEMPLATES[templateKey].definitions });
  };

  const loadCustomInstructionTemplate = (templateKey: keyof typeof CUSTOM_INSTRUCTION_TEMPLATES) => {
    const template = CUSTOM_INSTRUCTION_TEMPLATES[templateKey];
    updateFormData({ 
      custom_section_enabled: true,
      custom_section_title: template.title,
      custom_section_description: template.description
    });
  };

  // Custom Chat Completion Args (JSON) state
  const [chatArgsEnabled, setChatArgsEnabled] = React.useState(!!formData.chat_complete_request_args);
  const [chatArgsText, setChatArgsText] = React.useState(
    formData.chat_complete_request_args ? JSON.stringify(formData.chat_complete_request_args, null, 2) : ''
  );
  const [chatArgsError, setChatArgsError] = React.useState<string | null>(null);

  const handleChatArgsChange = (text: string) => {
    setChatArgsText(text);
    if (text.trim() === '') {
      setChatArgsError(null);
      // Keep cache empty if user clears it explicitly
      updateFormData({ chat_complete_request_args: null });
      return;
    }
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        setChatArgsError(null);
        updateFormData({ chat_complete_request_args: parsed });
      } else {
        setChatArgsError('JSON must be an object (e.g., { "temperature": 0.2 })');
      }
    } catch {
      setChatArgsError('Invalid JSON');
    }
  };

  // Add: handle Tab indentation inside textarea
  const handleChatArgsKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Tab') return;
    e.preventDefault();
    const target = e.currentTarget;
    const start = target.selectionStart ?? 0;
    const end = target.selectionEnd ?? 0;

    if (e.shiftKey) {
      const before = chatArgsText.slice(0, start);
      const after = chatArgsText.slice(end);
      let remove = 0;
      if (before.endsWith('  ')) remove = 2;
      else if (before.endsWith('\t')) remove = 1;
      if (remove > 0) {
        const newValue = chatArgsText.slice(0, start - remove) + after;
        setChatArgsText(newValue);
        handleChatArgsChange(newValue);
        requestAnimationFrame(() => {
          const pos = start - remove;
          target.setSelectionRange(pos, pos);
        });
      }
      return;
    }

    const indent = '  ';
    const newValue = chatArgsText.slice(0, start) + indent + chatArgsText.slice(end);
    setChatArgsText(newValue);
    handleChatArgsChange(newValue);
    requestAnimationFrame(() => {
      const pos = start + indent.length;
      target.setSelectionRange(pos, pos);
    });
  };

  const isFormValid = React.useCallback(() => {
    const baseValid = (
      formData.endpoint.trim() !== '' &&
      formData.token.trim() !== '' &&
      formData.workspace_or_extractor_id.trim() !== '' &&
      formData.account_type_definitions.some(def => def.name.trim() !== '') &&
      formData.field_definitions.some(def => def.name.trim() !== '') &&
      (!chatArgsEnabled || chatArgsError === null)
    );

    if (formData.custom_section_enabled) {
      return (
        baseValid &&
        formData.custom_section_title.trim() !== '' &&
        formData.custom_section_description.trim() !== ''
      );
    }

    return baseValid;
  }, [formData, chatArgsEnabled, chatArgsError]);

  React.useEffect(() => {
    // Do not clear cached JSON when toggling off; just clear the error
    if (!chatArgsEnabled) {
      setChatArgsError(null);
    }
  }, [chatArgsEnabled]);

  React.useEffect(() => {
    // Notify parent about validation state changes
    if (onValidationChange) {
      onValidationChange(isFormValid());
    }
  }, [formData, chatArgsEnabled, chatArgsError, onValidationChange, isFormValid]);

  // Expose validation method to parent via ref
  useImperativeHandle(ref, () => ({
    isValid: isFormValid
  }), [isFormValid]);

  const addEnumValue = (fieldIndex: number) => {
    const updatedDefinitions = formData.field_definitions.map((field, i) =>
      i === fieldIndex 
        ? { ...field, enum_values: [...(field.enum_values || []), ''] }
        : field
    );
    updateFormData({ field_definitions: updatedDefinitions });
  };

  const updateEnumValue = (fieldIndex: number, enumIndex: number, value: string) => {
    const updatedDefinitions = formData.field_definitions.map((field, i) =>
      i === fieldIndex 
        ? { 
            ...field, 
            enum_values: field.enum_values?.map((enumVal, j) => 
              j === enumIndex ? value : enumVal
            ) || []
          }
        : field
    );
    updateFormData({ field_definitions: updatedDefinitions });
  };

  const removeEnumValue = (fieldIndex: number, enumIndex: number) => {
    const updatedDefinitions = formData.field_definitions.map((field, i) =>
      i === fieldIndex 
        ? { 
            ...field, 
            enum_values: field.enum_values?.filter((_, j) => j !== enumIndex) || []
          }
        : field
    );
    updateFormData({ field_definitions: updatedDefinitions });
  };

  return (
    <form onSubmit={handleSubmit} className="request-form">
      <div className="form-section">
        <h3>Authentication</h3>
        <div className="form-group">
          <label htmlFor="auth-token">Auth Token:</label>
          <input
            id="auth-token"
            type="password"
            value={formData.auth_token}
            onChange={(e) => updateFormData({ auth_token: e.target.value })}
            placeholder="Enter your auth token"
            disabled={disabled}
          />
        </div>
      </div>

      <div className="form-section">
        <h3>FormX Configuration</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Endpoint *</label>
            <input
              type="url"
              value={formData.endpoint}
              onChange={(e) => updateFormData({ endpoint: e.target.value })}
              placeholder="https://api.formx.ai"
              required
              disabled={disabled}
            />
          </div>
          <div className="form-group">
            <label>Worker Token *</label>
            <input
              type="password"
              value={formData.token}
              onChange={(e) => updateFormData({ token: e.target.value })}
              placeholder="Your FormX worker token"
              required
              disabled={disabled}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Workspace/Extractor ID *</label>
            <input
              type="text"
              value={formData.workspace_or_extractor_id}
              onChange={(e) => updateFormData({ workspace_or_extractor_id: e.target.value })}
              placeholder="workspace_id or extractor_id"
              required
              disabled={disabled}
            />
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.is_workspace || false}
                onChange={(e) => updateFormData({ is_workspace: e.target.checked })}
                disabled={disabled}
              />
              Is Workspace
            </label>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>LLM Configuration</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Model Provider</label>
            <select
              value={formData.llm_model_provider || 'openrouter'}
              onChange={(e) => updateFormData({ llm_model_provider: e.target.value as 'openrouter' | 'google' })}
              disabled={disabled}
            >
              <option value="openrouter">OpenRouter</option>
              <option value="google">Google</option>
            </select>
          </div>
          <div className="form-group">
            <label>Model Name</label>
            <input
              type="text"
              value={formData.llm_model_name || ''}
              onChange={(e) => updateFormData({ llm_model_name: e.target.value || null })}
              placeholder="e.g., gpt-4"
              disabled={disabled}
            />
          </div>
        </div>
        {formData.llm_model_provider === 'openrouter' && (
          <div className="form-row form-group">
            <label>OpenRouter API Key</label>
            <input
              type="password"
              value={formData.openrouter_api_key || ''}
              onChange={(e) => updateFormData({ openrouter_api_key: e.target.value || null })}
              placeholder="Your OpenRouter API key"
              disabled={disabled}
            />
          </div>
        )}

        {/* Custom Chat Completion Request Args (JSON) */}
        <div className="form-row form-group">
          <label className="toggle-label">
            Custom Chat Completion Args (JSON)
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={chatArgsEnabled}
                onChange={(e) => setChatArgsEnabled(e.target.checked)}
                disabled={disabled}
                className="toggle-input"
              />
              <span className="toggle-slider"></span>
            </div>
          </label>
          <p className="form-help-text">
            Optional. Example: {"{ \"temperature\": 0.2, \"max_tokens\": 2048 }"}
          </p>
        </div>
        {chatArgsEnabled && (
          <div className="form-row form-group">
            <textarea
              value={chatArgsText}
              onChange={(e) => handleChatArgsChange(e.target.value)}
              onKeyDown={handleChatArgsKeyDown}
              placeholder='Enter JSON object, e.g. {"temperature": 0.2}'
              disabled={disabled}
              rows={6}
              style={{ resize: 'vertical', fontFamily: 'monospace' }}
            />
            {chatArgsError && <small style={{ color: '#dc2626' }}>{chatArgsError}</small>}
          </div>
        )}
      </div>

      <div className="form-section">
        <h3>Account Type Definitions *</h3>
        
        <div className="template-section">
          <h4>Quick Templates</h4>
          <div className="template-buttons">
            {Object.entries(ACCOUNT_TYPE_TEMPLATES).map(([key, template]) => (
              <button
                key={key}
                type="button"
                onClick={() => loadAccountTemplate(key as keyof typeof ACCOUNT_TYPE_TEMPLATES)}
                className="btn btn-template"
                disabled={disabled}
              >
                {template.displayName}
              </button>
            ))}
          </div>
        </div>

        {formData.account_type_definitions.map((accountType, index) => (
          <div key={index} className="document-type-row">
            <div className="form-group flex-grow">
              <input
                type="text"
                value={accountType.name}
                onChange={(e) => updateAccountType(index, 'name', e.target.value)}
                placeholder="Account type name"
                required
                disabled={disabled}
              />
            </div>
            <div className="form-group flex-grow">
              <textarea
                value={accountType.definition || ''}
                onChange={(e) => updateAccountType(index, 'definition', e.target.value)}
                placeholder="Account type definition (optional)"
                disabled={disabled}
                rows={2}
                style={{ resize: 'vertical' }}
              />
            </div>
            {formData.account_type_definitions.length > 1 && (
              <button
                type="button"
                onClick={() => removeAccountType(index)}
                className="btn btn-danger"
                disabled={disabled}
              >
                Remove
              </button>
            )}
          </div>
        ))}
        <div className="document-type-actions">
          <button
            type="button"
            onClick={addAccountType}
            className="btn btn-secondary"
            disabled={disabled}
          >
            Add Account Type
          </button>
        </div>
      </div>

      <div className="form-section">
        <h3>Field Definitions *</h3>
        
        <div className="template-section">
          <h4>Quick Templates</h4>
          <div className="template-buttons">
            {Object.entries(FIELD_TEMPLATES).map(([key, template]) => (
              <button
                key={key}
                type="button"
                onClick={() => loadFieldTemplate(key as keyof typeof FIELD_TEMPLATES)}
                className="btn btn-template"
                disabled={disabled}
              >
                {template.displayName}
              </button>
            ))}
          </div>
        </div>

        {formData.field_definitions.map((field, index) => (
          <div key={index} className="field-definition-row">
            <div className="form-row">
              <div className="form-group">
                <label>Field Name *</label>
                <input
                  type="text"
                  value={field.name}
                  onChange={(e) => updateField(index, 'name', e.target.value)}
                  placeholder="Field name"
                  required
                  disabled={disabled}
                />
              </div>
              <div className="form-group">
                <label>Field Type</label>
                <select
                  value={field.field_type || 'string'}
                  onChange={(e) => updateField(index, 'field_type', e.target.value)}
                  disabled={disabled}
                >
                  <option value="string">String</option>
                  <option value="number">Number</option>
                  <option value="date">Date</option>
                  <option value="boolean">Boolean</option>
                  <option value="time">Time</option>
                </select>
              </div>
              {formData.field_definitions.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeField(index)}
                  className="btn btn-danger"
                  disabled={disabled}
                >
                  Remove
                </button>
              )}
            </div>
            <div className="form-row form-group">
              <label>Field Description</label>
              <textarea
                value={field.description || ''}
                onChange={(e) => updateField(index, 'description', e.target.value)}
                placeholder="Field description (optional)"
                disabled={disabled}
                rows={2}
                style={{ resize: 'vertical' }}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={field.required || false}
                    onChange={(e) => updateField(index, 'required', e.target.checked)}
                    disabled={disabled}
                  />
                  Required Field
                </label>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={field.is_list || false}
                    onChange={(e) => updateField(index, 'is_list', e.target.checked)}
                    disabled={disabled}
                  />
                  List/Array Field
                </label>
              </div>
            </div>
            
            {/* Enum Values Section - Only show for string fields */}
            {(field.field_type === 'string' || !field.field_type) && (
              <div className="form-row">
                <div className="form-group">
                  <label>Enum Values (Optional)</label>
                  <p className="form-help-text">
                    Define specific allowed values for this field. Leave empty if any value is acceptable.
                  </p>
                  {field.enum_values && field.enum_values.length > 0 && (
                    <div className="enum-values-list">
                      {field.enum_values.map((enumValue, enumIndex) => (
                        <div key={enumIndex} className="enum-value-row">
                          <input
                            type="text"
                            value={enumValue}
                            onChange={(e) => updateEnumValue(index, enumIndex, e.target.value)}
                            placeholder={`Option ${enumIndex + 1}`}
                            disabled={disabled}
                          />
                          <button
                            type="button"
                            onClick={() => removeEnumValue(index, enumIndex)}
                            className="btn btn-danger btn-small"
                            disabled={disabled}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => addEnumValue(index)}
                    className="btn btn-secondary btn-small"
                    disabled={disabled}
                  >
                    Add Enum Value
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
        <div className="document-type-actions">
          <button
            type="button"
            onClick={addField}
            className="btn btn-secondary"
            disabled={disabled}
          >
            Add Field
          </button>
        </div>
      </div>

      <div className="form-section">
        <h3>Custom Extraction Instructions</h3>
        <div className="custom-instructions-header form-group">
          <label className="toggle-label">
            Enable Custom Instructions
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={formData.custom_section_enabled || false}
                onChange={(e) => updateFormData({ custom_section_enabled: e.target.checked })}
                disabled={disabled}
                className="toggle-input"
              />
              <span className="toggle-slider"></span>
            </div>
          </label>
        </div>
        
        {formData.custom_section_enabled && (
          <>
            <div className="template-section">
              <h4>Quick Templates</h4>
              <div className="template-buttons">
                {Object.entries(CUSTOM_INSTRUCTION_TEMPLATES).map(([key, template]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => loadCustomInstructionTemplate(key as keyof typeof CUSTOM_INSTRUCTION_TEMPLATES)}
                    className="btn btn-template"
                    disabled={disabled}
                  >
                    {template.displayName}
                  </button>
                ))}
              </div>
            </div>

            <p className="form-help-text">
              Add custom instructions to guide the AI in extracting specific information or handling special cases.
            </p>
            
            <div className="form-group">
              <label>Instruction Title *</label>
              <input
                type="text"
                value={formData.custom_section_title || ''}
                onChange={(e) => updateFormData({ custom_section_title: e.target.value })}
                placeholder="Brief title for your custom instructions"
                required={formData.custom_section_enabled}
                disabled={disabled}
              />
            </div>
            
            <div className="form-group">
              <label>Instruction Description *</label>
              <textarea
                value={formData.custom_section_description || ''}
                onChange={(e) => updateFormData({ custom_section_description: e.target.value })}
                placeholder="Detailed instructions for the AI&#10;&#10;Example:&#10;- Pay special attention to transaction descriptions containing 'TRANSFER'&#10;- Extract merchant category codes when available&#10;- Include any check numbers in the reference field"
                required={formData.custom_section_enabled}
                disabled={disabled}
                rows={8}
                style={{ resize: 'vertical' }}
              />
            </div>
            
            <div className="form-help-text">
              <small>
                These instructions will be formatted as a section in the AI prompt to help extract more accurate and relevant data from your bank statements.
              </small>
            </div>
          </>
        )}
      </div>

      {features.enableAdvancedOptions && (
        <div className="form-section">
          <h3>Processing Options</h3>
          <div className="form-row">
            <div className="form-group">
              <label>PDF DPI</label>
              <input
                type="number"
                value={formData.pdf_dpi || 150}
                onChange={(e) => updateFormData({ pdf_dpi: parseInt(e.target.value) })}
                min="72"
                max="300"
                disabled={disabled}
              />
            </div>
            <div className="form-group">
              <label>Batch Size</label>
              <input
                type="number"
                value={formData.batch_size || 10}
                onChange={(e) => updateFormData({ batch_size: parseInt(e.target.value) })}
                min="1"
                max="50"
                disabled={disabled}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.include_image || false}
                  onChange={(e) => updateFormData({ include_image: e.target.checked })}
                  disabled={disabled}
                />
                Include Image
              </label>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.include_text || false}
                  onChange={(e) => updateFormData({ include_text: e.target.checked })}
                  disabled={disabled}
                />
                Include Text
              </label>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.auto_adjust_image_size || false}
                  onChange={(e) => updateFormData({ auto_adjust_image_size: e.target.checked })}
                  disabled={disabled}
                />
                Auto Adjust Image Size
              </label>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.add_prompt || false}
                  onChange={(e) => updateFormData({ add_prompt: e.target.checked })}
                  disabled={disabled}
                />
                Add Prompt to Response
              </label>
            </div>
          </div>
        </div>
      )}
    </form>
  );
});
